/* import __COLOCATED_TEMPLATE__ from './sq-billboard.hbs'; */
/**

## Billboard

The billboard component is an attention-grabbing panel that can be used
like an advertisement to pull users into a conversion flow.

*IMPORTANT: In Dashboard, the billboard component is tied into [Jumbotron](https://fuse-docs.sqprod.co/services/jumbotron/overview.html)
and is used exclusively for upsells. Jumbotron will only ever show merchants
a single banner at a time. Using the billboard ember component on it's own
in another template for another purpose is HIGHLY DISCOURAGED, as it can
cause user-blindness to the UI pattern and negatively impact the effectiveness
of the billboard pattern as an upsell and tank conversion rates for these banners*

### Contextual Components

| Description | Contextual Component |
| --- | --- |
| Button | [sq-button](/components/glass/sq-button.html) |
| Media | [sq-billboard/media](/components/glass/sq-billboard/media.html) |

```example
@title Example with Raster Image

@controller-properties
{
  actions: {
    doSomething() {}
  }
}

@hbs
<SqBillboard @detail="Product/Vertical"
                @message="Get Alerts When Stock is Low"
                class="fuse-demo-billboard--image"
                @onDismiss={{action "doSomething"}}
                as |Billboard|>
  <Billboard.button-secondary @label="Secondary" @click={{action "doSomething"}} />
  <Billboard.button-primary @label="Primary" @click={{action "doSomething"}} />

  <Billboard.image @src={{hash 1x="/assets/images/square_card.png" 2x="/assets/images/square_card@2x.png"}} />
</SqBillboard>
```

### Theming

Theme your billboard by using the billboard mixin with your chosen color. You can pass a variable, hex or rgb color
to the mixin. The mixin will automatically choose whether or not text elements should be white/light or dark in color
based on the lightness of the color you pass to it.

```scss
$theme-color: #161B1F;

@include billboard-theme($theme-color);

```

```example
@title Examples with Raster Image & Art Direction/Positioning Options
@description You can pass `fullBleed` and `overlap` options to
`Billboard.image` to assist in art directing/positioning the image within
your billboard. `fullBleed` will make the image expand all the way to the
edges of the billboard container, while overlap will allow the image to
overlap underneath the text to the left a bit. You can adjust the position
using CSS positioning (top,left).

*If you're positioning the images on desktop with custom CSS, make sure to address it on mobile as well!*

@controller-properties
{
  actions: {
    doSomething() {}
  }
}

@hbs
<SqBillboard @detail="Product/Vertical"
                @message="Get Alerts When Stock is Low"
                class="billboard-with-bottom-aligned-image fuse-demo-billboard--image-art-direction"
                @onDismiss={{action "doSomething"}}
                as |Billboard|>
  <Billboard.button-secondary @label="Secondary" @click={{action "doSomething"}} />
  <Billboard.button-primary @label="Primary" @click={{action "doSomething"}} />

  <Billboard.image @src={{hash 1x="/assets/images/billboard-demo-image.png"
                              2x="/assets/images/billboard-demo-image@2x.png"}} />
</SqBillboard>

<SqBillboard @detail="Product/Vertical"
                @message="Get Alerts When Stock is Low"
                class="billboard-with-gradient-image fuse-demo-billboard--image-art-direction"
                @onDismiss={{action "doSomething"}}
                as |Billboard|>
  <Billboard.button-secondary @label="Secondary" @click={{action "doSomething"}} />
  <Billboard.button-primary @label="Primary" @click={{action "doSomething"}} />

  <Billboard.image @src={{hash 1x="/assets/images/square-reader-fade.png"
                              2x="/assets/images/square-reader-fade@2x.png"}}
                   @fullBleed=true />
</SqBillboard>

<SqBillboard @detail="Product/Vertical"
                @message="Get Alerts When Stock is Low"
                class="billboard-with-full-width-image fuse-demo-billboard--image-art-direction"
                @onDismiss={{action "doSomething"}}
                as |Billboard|>
  <Billboard.button-secondary @label="Secondary" @click={{action "doSomething"}} />
  <Billboard.button-primary @label="Primary" @click={{action "doSomething"}} />

  <Billboard.image @src={{hash 1x="/assets/images/square-hardware-pos-stand.png"
                               2x="/assets/images/square-hardware-pos-stand@2x.png"}}
                   @overlap=true
                   @fullBleed=true />
</SqBillboard>

```

```scss

$theme-color: #161B1F;

// Billboard with some slight customization
//===============================================
.billboard-with-bottom-aligned-image {
  // Include the theme
  @include billboard-theme($theme-color);
}

// Billboard with a full bleed image on the right
//===============================================
.billboard-with-gradient-image {
  // Include the theme
  @include billboard-theme($theme-color);

  //Need to make just the close icon dark since it's
  // floating over a light background from the image
  @include billboard-close-icon(rgba(0, 0, 0, 0.4));
}

// A really customized billboard
//================================================
.billboard-with-full-width-image {
  // Include the theme (use a light theme since we know the image will have a light background)
  @include billboard-theme($color-white);

  // Position the image differently than the default (centered)
  .billboard__media_image img {
    top: 60%;
    transform: translate(0,-60%);
  }

  // Set the button to be the theme color since our image has neutral tones
  .billboard__button--primary {
    background-color: $theme-color;
    color: $color-white;
  }
}
```

@param {String} detail - Localized string for the small caps text line
@param {String} message - Localized string for the larger main copy line
@param {String} dismissButtonAriaLabel - Localized string for the dismiss button aria label

See the [billboard media component](/components/glass/sq-billboard/sq-billboard-media.html)
for art-direction params (`fullBleed`, `overlap`)
Style dependencies at: `@square/glass-ui/addon/styles/@square/messaging/billboard`

*/

import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['billboard'],
  detail: null,
  message: null,
  dismissButtonAriaLabel: null,
  actions: {
    dismiss() {
      if (this.onDismiss) {
        this.onDismiss();
      }
    }
  }
});